import React, { useState, useEffect } from 'react';
import {
    User, Mail, Briefcase, Building2, Plus, ArrowLeft, X, Save, Clock, Calendar,
    Edit, Trash, RefreshCw, CreditCard, Fingerprint, Smile, Key, Hand, Monitor,
    Settings as SettingsIcon, Wifi, WifiOff, CheckCircle2, AlertTriangle, Radio,
    Zap, ArrowUpRight, Check, AlertCircle, Info, Download
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';
import ProfileImageUpload from '../component/ProfileImageUpload';

/* ---------------- REUSABLE INPUT ---------------- */
const InputField = ({ label, value, onChange, disabled }) => (
    <div>
        <label className="text-sm font-semibold">{label}</label>
        <input
            type="text"
            value={value || ""}
            onChange={onChange}
            disabled={disabled}
            className={`w-full mt-1 border rounded-lg px-3 py-2 ${disabled ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
        />
    </div>
);

const ManageEmployees = () => {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [designations, setDesignations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        mobile_number: '',
        country_code: '+91',
        gender: 'Male',
        date_of_birth: '',
        designation_id_FK: '',
        department_id_FK: '',
        rfid_serial_number: '',
        device_password: ''
    });
    const [formError, setFormError] = useState('');
    const [showShiftModal, setShowShiftModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [shiftData, setShiftData] = useState({
        shift_name: '',
        start_time: '',
        end_time: '',
        working_days: []
    });
    const [shiftLoading, setShiftLoading] = useState(false);
    const [shiftSaving, setShiftSaving] = useState(false);
    const [shiftError, setShiftError] = useState('');
    const [hasShift, setHasShift] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showConfirmModalweb, setShowConfirmModalweb] = useState(false);
    const [pendingEmployee, setPendingEmployee] = useState(null);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [showCLPLModal, setShowCLPLModal] = useState(false);
    const [clplEmployee, setClplEmployee] = useState(null);
    const [clplData, setClplData] = useState({
        quarter: 'Q1',
        year: new Date().getFullYear(),
        totalCL: 2,
        totalPL: 2
    });
    const [clplSaving, setClplSaving] = useState(false);
    const [clplError, setClplError] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [rfidList, setRfidList] = useState([]);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [departmentsMaster, setDepartmentsMaster] = useState([]);
    const [designationsMaster, setDesignationsMaster] = useState([]);
    const [syncingToMachine, setSyncingToMachine] = useState(false);
    const [pullingFromMachine, setPullingFromMachine] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [deletingEmployee, setDeletingEmployee] = useState(false);

    // 📡 Live Biometric Hardware Health & Real-time Feedback State
    const [biometricHealth, setBiometricHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);
    const [feedbackToast, setFeedbackToast] = useState(null);
    const [claimingDevice, setClaimingDevice] = useState(false);

    const showFeedbackToast = ({ type, title, message }) => {
        setFeedbackToast({ type, title, message });
        setTimeout(() => setFeedbackToast(null), 8000);
    };

    const fetchBiometricHealth = async () => {
        try {
            setHealthLoading(true);
            const res = await axiosInstance.get('/api/organization/biometric-health');
            if (res.data?.status) {
                setBiometricHealth(res.data.data);
            }
        } catch (e) {
            console.warn('Failed to load biometric health:', e.message);
        } finally {
            setHealthLoading(false);
        }
    };

    const handlePingDevice = async (devId) => {
        try {
            const res = await axiosInstance.post(`/api/organization/ping-machine/${devId}`);
            showFeedbackToast({
                type: 'info',
                title: 'Ping Command Dispatched',
                message: res.data?.message || `Ping queued for machine ${devId}. Heartbeat will update in a few seconds.`
            });
            setTimeout(fetchBiometricHealth, 2500);
        } catch (e) {
            showFeedbackToast({
                type: 'error',
                title: 'Ping Failed',
                message: e.response?.data?.message || 'Error pinging device'
            });
        }
    };

    const handleClaimUnassignedDevice = async (devId) => {
        if (!window.confirm(`Assign biometric machine ${devId} to your company and sync all active employees to it?`)) return;
        try {
            setClaimingDevice(true);
            const res = await axiosInstance.post('/api/organization/assign-my-device', { dev_id: devId });
            showFeedbackToast({
                type: 'success',
                title: 'Machine Linked Successfully',
                message: res.data?.message || `Machine ${devId} is now assigned to your company! Synchronizing staff...`
            });
            await fetchBiometricHealth();
            await fetchEmployees();
        } catch (e) {
            showFeedbackToast({
                type: 'error',
                title: 'Assignment Failed',
                message: e.response?.data?.message || 'Error assigning machine'
            });
        } finally {
            setClaimingDevice(false);
        }
    };

    const handleSyncWithMachine = async () => {
        try {
            setSyncingToMachine(true);
            const res = await axiosInstance.post('/api/organization/sync-machines');
            if (res.data?.status) {
                showFeedbackToast({
                    type: 'success',
                    title: 'Sync Enqueued for All Employees',
                    message: res.data?.message || 'All active employees queued for sync! Connected machines will update in ~5-15 seconds.'
                });
                setTimeout(() => {
                    fetchEmployees();
                    fetchBiometricHealth();
                }, 3000);
            } else {
                showFeedbackToast({
                    type: 'error',
                    title: 'Sync Failed',
                    message: res.data?.message || 'Sync failed'
                });
            }
        } catch (e) {
            showFeedbackToast({
                type: 'error',
                title: 'Sync Error',
                message: e.response?.data?.message || 'Error syncing to machine'
            });
        } finally {
            setSyncingToMachine(false);
        }
    };

    const handlePullFromMachine = async () => {
        try {
            setPullingFromMachine(true);
            const res = await axiosInstance.post('/api/organization/pull-machine-users');
            if (res.data?.status) {
                showFeedbackToast({
                    type: 'success',
                    title: 'Machine Pull Dispatched',
                    message: res.data?.message || 'Pulling all employees and attendance logs from machine! Fetching profiles...'
                });
                setTimeout(() => {
                    fetchEmployees();
                    fetchBiometricHealth();
                }, 3000);
                setTimeout(() => {
                    fetchEmployees();
                }, 8000);
            } else {
                showFeedbackToast({
                    type: 'error',
                    title: 'Pull Failed',
                    message: res.data?.message || 'Failed to pull data from machine'
                });
            }
        } catch (e) {
            showFeedbackToast({
                type: 'error',
                title: 'Pull Error',
                message: e.response?.data?.message || 'Error pulling from biometric machine'
            });
        } finally {
            setPullingFromMachine(false);
        }
    };

    const handleDeleteClick = (emp) => {
        setEmployeeToDelete(emp);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!employeeToDelete) return;
        try {
            setDeletingEmployee(true);
            const res = await axiosInstance.delete(`/api/users/${employeeToDelete.id}`);
            showFeedbackToast({
                type: 'success',
                title: 'Employee Deleted',
                message: res.data?.message || `Employee ${employeeToDelete.name} deleted successfully and unlinked from biometric machines.`
            });
            setShowDeleteModal(false);
            setEmployeeToDelete(null);
            await fetchEmployees();
            await fetchBiometricHealth();
        } catch (err) {
            showFeedbackToast({
                type: 'error',
                title: 'Delete Failed',
                message: err.response?.data?.message || err.message || 'Error deleting employee'
            });
        } finally {
            setDeletingEmployee(false);
        }
    };

    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const fetchEmployees = async () => {
        const token = localStorage.getItem("token");
        if (!token || !token.trim()) {
            setError('Please login to access this page.');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const response = await axiosInstance.get('/api/users');
            const employeesData = response.data?.data || [];

            const transformedEmployees = employeesData.map((emp) => ({
                id: emp.user_id_PK,
                name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Unknown',
                email: emp.email || emp.personal_email || 'N/A',
                designation: emp.designation_name || 'N/A',
                department: emp.department_name || 'N/A',
                gender: emp.gender || 'N/A',
                mobile: emp.mobile_number ? `${emp.country_code || ''} ${emp.mobile_number}`.trim() : 'N/A',
                dateOfBirth: emp.date_of_birth || 'N/A',
                avatar: emp.user_profile_img,
                isActive: emp.is_active === 0,
                employeeId: emp.employeeId || 'N/A',
                role: emp.role || 'N/A',
                rfid_serial_number: emp.rfid_serial_number || '',
                createdAt: emp.created_at || 'N/A',
                web_status: emp.web_status === 0,
                biometrics: emp.biometrics || null,
                biometric_sync: emp.biometric_sync || null,

                // Keep raw data for edit modal
                rawData: emp
            }));

            setEmployees(transformedEmployees);
        } catch (err) {
            console.error('Error fetching employees:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load employees.');
        } finally {
            setLoading(false);
        }
    };

    const fetchDesignations = async () => {
        try {
            const response = await axiosInstance.get('/api/designation');
            console.log('Designations API Response:', response.data);

            let designationsData = [];
            if (response.data?.status && Array.isArray(response.data?.data)) {
                designationsData = response.data.data;
            } else if (Array.isArray(response.data?.data)) {
                designationsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                designationsData = response.data;
            }

            console.log('Parsed Designations:', designationsData);
            setDesignations(designationsData);
            setDesignationsMaster(designationsData); // Store for edit modal
        } catch (err) {
            console.error('Error fetching designations:', err);
            console.error('Error details:', err.response?.data);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axiosInstance.get('/api/departments');
            console.log('Departments API Response:', response.data);

            let departmentsData = [];
            if (response.data?.status && Array.isArray(response.data?.data)) {
                departmentsData = response.data.data;
            } else if (Array.isArray(response.data?.data)) {
                departmentsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                departmentsData = response.data;
            }

            console.log('Parsed Departments:', departmentsData);
            setDepartments(departmentsData);
            setDepartmentsMaster(departmentsData); // Store for edit modal
        } catch (err) {
            console.error('Error fetching departments:', err);
            console.error('Error details:', err.response?.data);
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchDesignations();
        fetchDepartments();
        fetchRfidList();
        fetchBiometricHealth();

        // Auto-poll RFID list every 5 seconds
        const rfidPollingInterval = setInterval(() => {
            fetchRfidList();
        }, 5000);

        // Auto-poll Biometric Hardware Health every 8 seconds
        const healthPollingInterval = setInterval(() => {
            fetchBiometricHealth();
        }, 8000);

        // Cleanup intervals on component unmount
        return () => {
            clearInterval(rfidPollingInterval);
            clearInterval(healthPollingInterval);
        };
    }, []);



    const handleAddEmployee = () => {
        setShowAddModal(true);
        setFormError('');
        setFormData({
            first_name: '',
            last_name: '',
            email: '',
            mobile_number: '',
            country_code: '+91',
            gender: 'Male',
            date_of_birth: '',
            designation_id_FK: '',
            department_id_FK: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormError('');
    };

    const validateForm = () => {
        if (!formData.first_name.trim()) {
            setFormError('First name is required');
            return false;
        }
        if (!formData.last_name.trim()) {
            setFormError('Last name is required');
            return false;
        }
        if (!formData.email.trim()) {
            setFormError('Email is required');
            return false;
        }
        if (!formData.mobile_number.trim()) {
            setFormError('Mobile number is required');
            return false;
        }
        if (!formData.date_of_birth) {
            setFormError('Date of birth is required');
            return false;
        }
        if (!formData.designation_id_FK) {
            setFormError('Designation is required');
            return false;
        }
        if (!formData.department_id_FK) {
            setFormError('Department is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setSaving(true);
            setFormError('');

            const payload = {
                gender: formData.gender,
                mobile_number: formData.mobile_number,
                country_code: formData.country_code,
                date_of_birth: formData.date_of_birth,
                email: formData.email,
                designation_id_FK: formData.designation_id_FK,
                department_id_FK: formData.department_id_FK,
                first_name: formData.first_name,
                last_name: formData.last_name,
                rfid_serial_number: formData.rfid_serial_number ? formData.rfid_serial_number.trim() : null,
                device_password: formData.device_password ? formData.device_password.trim() : null
            };

            const response = await axiosInstance.post('/api/users', payload);

            if (response.data?.status) {
                setShowAddModal(false);
                fetchEmployees();
                fetchBiometricHealth();

                const bioSync = response.data?.biometric_sync;
                if (bioSync) {
                    if (bioSync.status === 'ONLINE_DELIVERING') {
                        showFeedbackToast({
                            type: 'success',
                            title: 'Employee Created & Pushed to Machine',
                            message: `Machine (${bioSync.device_id}) is ONLINE! Employee credentials sent to hardware. Employee can now enroll fingerprint / face on the physical device.`
                        });
                    } else if (bioSync.status === 'QUEUED_OFFLINE') {
                        showFeedbackToast({
                            type: 'warning',
                            title: 'Employee Created (Device Currently Offline)',
                            message: `Machine (${bioSync.device_id}) has no internet/WiFi connection. Command is queued safely in cloud and will push automatically the moment machine reconnects.`
                        });
                    } else if (bioSync.status === 'NO_DEVICE') {
                        showFeedbackToast({
                            type: 'info',
                            title: 'Employee Created (No Biometric Machine Assigned)',
                            message: 'Employee record created in database. Note: No biometric hardware is assigned to this company.'
                        });
                    } else {
                        showFeedbackToast({
                            type: 'success',
                            title: 'Employee Created Successfully',
                            message: response.data?.message || 'Employee created successfully.'
                        });
                    }
                } else {
                    showFeedbackToast({
                        type: 'success',
                        title: 'Employee Created Successfully',
                        message: response.data?.message || 'Employee created successfully.'
                    });
                }

                setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    mobile_number: '',
                    country_code: '+91',
                    gender: 'Male',
                    date_of_birth: '',
                    designation_id_FK: '',
                    department_id_FK: '',
                    rfid_serial_number: '',
                    device_password: ''
                });
            } else {
                setFormError(response.data?.message || 'Failed to add employee');
            }
        } catch (err) {
            console.error('Error adding employee:', err);
            setFormError(err.response?.data?.message || err.message || 'Failed to add employee. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleManageShift = async (employee) => {
        setSelectedEmployee(employee);
        setShowShiftModal(true);
        setShiftError('');
        setHasShift(false);
        setShiftData({
            shift_name: '',
            start_time: '',
            end_time: '',
            working_days: []
        });

        // Fetch existing shift
        try {
            setShiftLoading(true);
            const response = await axiosInstance.get(`/api/shift/${employee.id}`);

            if (response.data?.status && response.data?.data) {
                const shift = response.data.data;
                setHasShift(true);
                setShiftData({
                    shift_name: shift.shift_name || '',
                    start_time: shift.start_time || '',
                    end_time: shift.end_time || '',
                    working_days: shift.working_days ? shift.working_days.split(',') : []
                });
            } else {
                setHasShift(false);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                // No shift exists, that's okay
                setHasShift(false);
            } else {
                console.error('Error fetching shift:', err);
                setShiftError('Failed to load shift data');
            }
        } finally {
            setShiftLoading(false);
        }
    };

    const handleConfirmStatusChange = async () => {
        if (!pendingEmployee) return;

        try {
            await axiosInstance.put(`/api/users/users/${pendingEmployee.id}`, {
                is_active: pendingStatus ? 0 : 1
            });
            // Refresh employee list
            fetchEmployees();
            setShowConfirmModal(false);
            setPendingEmployee(null);
            setPendingStatus(null);
        } catch (err) {
            console.error('Error updating employee status:', err);
            setShowConfirmModal(false);
            setPendingEmployee(null);
            setPendingStatus(null);
        }
    };


    const handleConfirmWebChakingStatusChange = async () => {
        if (!pendingEmployee) return;

        try {
            await axiosInstance.put(`/api/users/users/web_status/${pendingEmployee.id}`, {
                web_status: pendingStatus ? 0 : 1
            });
            // Refresh employee list
            fetchEmployees();
            setShowConfirmModalweb(false);
            setPendingEmployee(null);
            setPendingStatus(null);
            await fetchEmployees();
        } catch (err) {
            console.error('Error updating employee status:', err);
            setShowConfirmModalweb(false);
            setPendingEmployee(null);
            setPendingStatus(null);
        }
    };

    const handleWorkingDayToggle = (day) => {
        setShiftData(prev => {
            const days = [...prev.working_days];
            if (days.includes(day)) {
                return { ...prev, working_days: days.filter(d => d !== day) };
            } else {
                return { ...prev, working_days: [...days, day] };
            }
        });
    };

    const handleSaveShift = async () => {
        if (!selectedEmployee) return;

        if (!shiftData.shift_name.trim()) {
            setShiftError('Shift name is required');
            return;
        }
        if (!shiftData.start_time) {
            setShiftError('Start time is required');
            return;
        }
        if (!shiftData.end_time) {
            setShiftError('End time is required');
            return;
        }
        if (shiftData.working_days.length === 0) {
            setShiftError('Please select at least one working day');
            return;
        }

        try {
            setShiftSaving(true);
            setShiftError('');

            const payload = {
                shift_name: shiftData.shift_name,
                start_time: shiftData.start_time,
                end_time: shiftData.end_time,
                working_days: shiftData.working_days.join(',')
            };

            const response = await axiosInstance.post(`/api/shift/${selectedEmployee.id}`, payload);

            if (response.data?.status) {
                setShowShiftModal(false);
                setHasShift(true);
                // Refresh employee list
                fetchEmployees();
            } else {
                setShiftError(response.data?.message || 'Failed to save shift');
            }
        } catch (err) {
            console.error('Error saving shift:', err);
            setShiftError(err.response?.data?.message || err.message || 'Failed to save shift');
        } finally {
            setShiftSaving(false);
        }
    };

    const handleAddCLPL = (employee) => {
        setClplEmployee(employee);
        setClplData({
            quarter: 'Q1',
            year: new Date().getFullYear(),
            totalCL: 2,
            totalPL: 2
        });
        setClplError('');
        setShowCLPLModal(true);
    };

    const handleSaveCLPL = async () => {
        if (!clplEmployee) return;

        if (!clplData.quarter || !clplData.year || clplData.totalCL < 0 || clplData.totalPL < 0) {
            setClplError('Please fill all fields correctly');
            return;
        }

        try {
            setClplSaving(true);
            setClplError('');

            const payload = {
                userId: clplEmployee.id,
                quarter: clplData.quarter,
                year: Number(clplData.year),
                totalCL: Number(clplData.totalCL),
                totalPL: Number(clplData.totalPL)
            };

            const response = await axiosInstance.post('/api/add/clpl', payload);

            if (response.data?.message) {
                setShowCLPLModal(false);
                setClplEmployee(null);
                // Optionally refresh employee list or show success message
                alert('CL/PL added successfully!');
            } else {
                setClplError('Failed to add CL/PL');
            }
        } catch (err) {
            console.error('Error adding CL/PL:', err);
            setClplError(err.response?.data?.message || err.message || 'Failed to add CL/PL');
        } finally {
            setClplSaving(false);
        }
    };

    /* ---------------- FIXED: EDIT PROFILE MODAL FUNCTION ---------------- */
    const handleOpenProfileModal = async (employeeId) => {
        try {
            setSelectedEmployeeId(employeeId);
            setLoading(true);

            // Fetch specific employee details
            const response = await axiosInstance.get(`/api/users/${employeeId}`);

            if (response.data?.status && response.data?.data) {
                const employeeData = response.data.data;

                // Format the data for the modal
                setProfileData({
                    id: employeeData.user_id_PK,
                    first_name: employeeData.first_name || '',
                    last_name: employeeData.last_name || '',
                    email: employeeData.email || '',
                    gender: employeeData.gender || '',
                    date_of_birth: employeeData.date_of_birth ?
                        employeeData.date_of_birth.split('T')[0] : '',
                    department_name: employeeData.department_name || 'N/A',
                    designation_name: employeeData.designation_name || 'N/A',
                    blood_group: employeeData.blood_group || 'N/A',
                    rfid_serial_number: employeeData.rfid_serial_number || '',
                    device_password: employeeData.device_password || '',
                    fingerprint: employeeData.fingerprint || 0,
                    face_login: employeeData.face_login || 0,
                    palm_login: employeeData.palm_login || 0,
                    mobile_number: employeeData.mobile_number || '',
                    emergency_contact_no: employeeData.emergency_contact_no || '',
                    country_code: employeeData.country_code || '+91'
                });

                setShowEditModal(true);
            } else {
                alert('Failed to fetch employee details');
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
            alert('Error loading employee profile');
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- FETCH RFID LIST (FIXED) ---------------- */
    const fetchRfidList = async () => {
        try {
            const token = localStorage.getItem("token");
            const res = await axiosInstance.get("/api/rfid/list", {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setRfidList(res.data?.data || []);
        } catch (err) {
            console.error("RFID Error", err);
        }
    };

    /* ---------------- SAVE PROFILE ---------------- */
    const handleSaveProfile = async () => {
        if (!profileData || !selectedEmployeeId) return;

        try {
            setSaving(true);

            const payload = {
                rfid_serial_number: profileData.rfid_serial_number ? profileData.rfid_serial_number.trim() : null,
                device_password: profileData.device_password ? profileData.device_password.trim() : null,
                fingerprint: profileData.fingerprint,
                face_login: profileData.face_login,
                palm_login: profileData.palm_login,
                mobile_number: profileData.mobile_number,
                emergency_contact_no: profileData.emergency_contact_no,
                country_code: profileData.country_code
            };

            const res = await axiosInstance.patch(`/api/users/${selectedEmployeeId}`, payload);

            setShowEditModal(false);
            fetchEmployees();
            fetchBiometricHealth();

            const bioSync = res.data?.biometric_sync;
            if (bioSync) {
                if (bioSync.status === 'ONLINE_DELIVERING') {
                    showFeedbackToast({
                        type: 'success',
                        title: 'Profile Updated & Pushed to Machine',
                        message: `Machine (${bioSync.device_id}) is ONLINE. Updated profile dispatched to hardware.`
                    });
                } else if (bioSync.status === 'QUEUED_OFFLINE') {
                    showFeedbackToast({
                        type: 'warning',
                        title: 'Profile Updated (Device Offline)',
                        message: `Machine (${bioSync.device_id}) has no internet/WiFi. Updates safely queued in cloud and will sync automatically when online.`
                    });
                } else {
                    showFeedbackToast({
                        type: 'success',
                        title: 'Profile Updated Successfully',
                        message: 'Employee updated successfully.'
                    });
                }
            } else {
                showFeedbackToast({
                    type: 'success',
                    title: 'Profile Updated',
                    message: 'Employee updated successfully.'
                });
            }
        } catch (err) {
            console.error('Error updating profile:', err);
            showFeedbackToast({
                type: 'error',
                title: 'Update Failed',
                message: err.response?.data?.message || err.message || 'Failed to update employee'
            });
        } finally {
            setSaving(false);
        }
    };


    const handleDeleteRfid = async () => {
        if (!selectedEmployeeId) return;

        const payload = {
            rfid_serial_number: profileData.rfid_serial_number,
            device_id: "UP1A.231005.007",
        };

        const confirmDelete = window.confirm(
            "Are you sure you want to remove this RFID?"
        );

        if (!confirmDelete) return;

        try {
            setSaving(true);

            const token = localStorage.getItem("token");

            await axiosInstance.post(
                "/api/rfid/rfidCMD/delete",
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setProfileData((prev) => ({
                ...prev,
                rfid_serial_number: "",
            }));

            alert("RFID removed successfully");
        } catch (err) {
            console.error("RFID delete error", err);
            alert(err.response?.data?.message || "Failed to remove RFID");
        } finally {
            setSaving(false);
        }
    };



    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <div className="max-w-7xl mx-auto p-6">
                {/* Header Section */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-800 rounded-2xl shadow-xl p-8 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                            >
                                <ArrowLeft size={24} />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold flex items-center gap-3">
                                    <User size={32} />
                                    Manage Employees
                                </h1>
                                <p className="text-blue-100 mt-2">View and manage all employees in your organization</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/biometric-settings')}
                                className="flex items-center gap-2 px-4 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors font-medium shadow-md border border-white/30 text-sm backdrop-blur-sm"
                                title="Open Biometric Machine Cloud Settings (Time, IP, Name, Door, Maintenance)"
                            >
                                <Monitor size={18} />
                                Machine Settings
                            </button>
                            <button
                                onClick={handleSyncWithMachine}
                                disabled={syncingToMachine}
                                className="flex items-center gap-2 px-4 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors font-medium shadow-md border border-white/30 text-sm backdrop-blur-sm"
                                title="Sync all active employees to connected biometric machines"
                            >
                                <RefreshCw size={18} className={syncingToMachine ? 'animate-spin' : ''} />
                                {syncingToMachine ? 'Syncing...' : 'Sync to Machine'}
                            </button>
                            <button
                                onClick={handlePullFromMachine}
                                disabled={pullingFromMachine}
                                className="flex items-center gap-2 px-4 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors font-medium shadow-md border border-white/30 text-sm backdrop-blur-sm"
                                title="Pull all employees, biometric templates, and punch logs directly from the machine into cloud"
                            >
                                <Download size={18} className={pullingFromMachine ? 'animate-bounce' : ''} />
                                {pullingFromMachine ? 'Pulling...' : 'Pull from Machine'}
                            </button>
                            <button
                                onClick={handleAddEmployee}
                                className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-lg text-sm"
                            >
                                <Plus size={20} />
                                Add Employee
                            </button>
                        </div>
                    </div>
                </div>

                {/* 📡 Biometric Hardware Diagnostic & Sync Control Hub */}
                <div className="mb-6 space-y-4">
                    {/* Hardware Health Status Cards */}
                    {biometricHealth?.has_devices ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {biometricHealth.assigned_devices.map((dev) => (
                                <div
                                    key={dev.dev_id}
                                    className={`rounded-2xl p-5 border shadow-sm transition-all ${
                                        dev.is_online
                                            ? 'bg-gradient-to-br from-emerald-50/90 via-white to-teal-50/40 border-emerald-300'
                                            : 'bg-gradient-to-br from-rose-50/90 via-white to-amber-50/40 border-rose-300'
                                    }`}
                                >
                                    {/* Card Top: Status Dot & Serial */}
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="relative flex h-3.5 w-3.5">
                                                {dev.is_online && (
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                                )}
                                                <span
                                                    className={`relative inline-flex rounded-full h-3.5 w-3.5 ${
                                                        dev.is_online ? 'bg-emerald-500' : 'bg-rose-500'
                                                    }`}
                                                ></span>
                                            </span>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md font-mono ${
                                                            dev.is_online
                                                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                                                : 'bg-rose-100 text-rose-800 border border-rose-200'
                                                        }`}
                                                    >
                                                        {dev.is_online ? 'ONLINE' : 'OFFLINE'}
                                                    </span>
                                                    <span className="text-xs font-medium text-gray-500">
                                                        {dev.is_online
                                                            ? `Ping: ${dev.sec_since_ping}s ago`
                                                            : `Last seen: ${dev.sec_since_ping ? Math.round(dev.sec_since_ping / 60) + ' min ago' : 'Never'}`}
                                                    </span>
                                                </div>
                                                <h4 className="text-base font-bold text-gray-900 mt-1 font-mono flex items-center gap-1.5">
                                                    <Monitor size={16} className={dev.is_online ? 'text-emerald-600' : 'text-rose-600'} />
                                                    {dev.dev_id}
                                                </h4>
                                            </div>
                                        </div>

                                        {/* Action Icons */}
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => handlePingDevice(dev.dev_id)}
                                                title="Send Test Ping Command"
                                                className="p-1.5 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-white border border-gray-200 transition-colors shadow-2xs"
                                            >
                                                <Zap size={15} />
                                            </button>
                                            <button
                                                onClick={() => navigate(`/biometric-settings?dev_id=${dev.dev_id}`)}
                                                title="Open Machine Cloud Settings"
                                                className="p-1.5 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-white border border-gray-200 transition-colors shadow-2xs"
                                            >
                                                <SettingsIcon size={15} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Card Details */}
                                    <div className="mt-3.5 pt-3 border-t border-gray-100 text-xs text-gray-600 space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-500">Device IP:</span>
                                            <span className="font-mono font-medium text-gray-800">{dev.ip || 'DHCP'}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-500">Server Target:</span>
                                            <span className="font-mono font-medium text-blue-700">168.144.28.127:4002</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-500">Cloud Sync Queue:</span>
                                            <span className={`font-semibold ${dev.pending_commands > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                {dev.pending_commands} command{dev.pending_commands === 1 ? '' : 's'} waiting
                                            </span>
                                        </div>

                                        {!dev.is_online && (
                                            <div className="mt-2.5 p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-[11px] leading-relaxed">
                                                <strong>⚠️ Device Disconnected:</strong> Machine is not reaching server. Check machine power cable and ensure WiFi is connected. Commands remain safely queued in cloud.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-amber-900 shadow-sm">
                            <div className="flex items-center gap-3">
                                <AlertTriangle size={24} className="text-amber-600 shrink-0" />
                                <div>
                                    <h4 className="text-sm font-bold">No Biometric Hardware Linked</h4>
                                    <p className="text-xs text-amber-700 mt-0.5">
                                        No machine is assigned to your company. Biometric attendance punches are inactive.
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/biometric-settings')}
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors whitespace-nowrap"
                            >
                                Link Hardware
                            </button>
                        </div>
                    )}
                </div>

                {/* Content Section */}
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                    {/* Error Message */}
                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Loading State */}
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : employees.length === 0 ? (
                        <div className="text-center py-12">
                            <User className="mx-auto text-gray-400 mb-4" size={48} />
                            <p className="text-gray-600 text-lg">No employees found</p>
                        </div>
                    ) : (
                        /* Employee List Table */
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Employee</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Biometrics (Machine)</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Contact</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Designation</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Department</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {employees.map((emp) => (
                                        <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-3">
                                                    {emp.avatar && emp.avatar.startsWith('http') ? (
                                                        <img
                                                            src={emp.avatar}
                                                            alt={emp.name}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextSibling.style.display = 'flex';
                                                            }}
                                                            className="w-10 h-10 rounded-full object-cover"
                                                        />
                                                    ) : null}
                                                    <div className={`w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center ${emp.avatar && emp.avatar.startsWith('http') ? 'hidden' : ''}`}>
                                                        <User className="text-blue-600" size={20} />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900">{emp.name}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {emp.employeeId !== 'N/A' ? `Emp ID: ${emp.employeeId}` : `ID: ${emp.id}`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="space-y-1.5 max-w-xs">
                                                    {/* 1. Hardware Sync Delivery Status Badge */}
                                                    <div>
                                                        {emp.biometric_sync?.status === 'SYNCED' ? (
                                                            <span
                                                                title={emp.biometric_sync?.description || 'Active in biometric machine memory'}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 shadow-2xs"
                                                            >
                                                                <CheckCircle2 size={12} className="text-emerald-600" />
                                                                Synced to Machine
                                                            </span>
                                                        ) : emp.biometric_sync?.status === 'QUEUED_OFFLINE' ? (
                                                            <span
                                                                title={emp.biometric_sync?.description || 'Machine is offline. Queued in cloud.'}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300 shadow-2xs"
                                                            >
                                                                <Clock size={12} className="text-amber-700 animate-pulse" />
                                                                Sync Queued (Device Offline)
                                                            </span>
                                                        ) : emp.biometric_sync?.status === 'DELIVERING' ? (
                                                            <span
                                                                title="Device is online. Command being picked up by machine."
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-300 shadow-2xs"
                                                            >
                                                                <RefreshCw size={12} className="animate-spin text-blue-600" />
                                                                Delivering to Machine...
                                                            </span>
                                                        ) : emp.biometric_sync?.status === 'FAILED' ? (
                                                            <span
                                                                title={emp.biometric_sync?.description || 'Failed on machine'}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-900 border border-rose-300 shadow-2xs"
                                                            >
                                                                <AlertCircle size={12} className="text-rose-600" />
                                                                Sync Failed
                                                            </span>
                                                        ) : (
                                                            <span
                                                                title={emp.biometric_sync?.description || 'No machine bound'}
                                                                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200"
                                                            >
                                                                No Machine Bound
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* 2. Enrolled Biometric Credentials Badges */}
                                                    <div className="flex flex-wrap items-center gap-1">
                                                        {emp.biometrics?.rfid && (
                                                            <span
                                                                title="RFID Card Registered on Device"
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                            >
                                                                <CreditCard size={11} className="text-emerald-600" />
                                                                Card
                                                            </span>
                                                        )}
                                                        {emp.biometrics?.fingerprint && (
                                                            <span
                                                                title={`Fingerprint Enrolled on Device (${emp.biometrics.fp_count || 1} FP)`}
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                                                            >
                                                                <Fingerprint size={11} className="text-blue-600" />
                                                                FP {emp.biometrics.fp_count > 1 ? `(${emp.biometrics.fp_count})` : ''}
                                                            </span>
                                                        )}
                                                        {emp.biometrics?.face && (
                                                            <span
                                                                title="Face 3D Template Enrolled"
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200"
                                                            >
                                                                <Smile size={11} className="text-purple-600" />
                                                                Face ID
                                                            </span>
                                                        )}
                                                        {emp.biometrics?.palm && (
                                                            <span
                                                                title="Palm Vein Enrolled"
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                                                            >
                                                                <Hand size={11} className="text-amber-600" />
                                                                Palm
                                                            </span>
                                                        )}
                                                        {emp.biometrics?.password && (
                                                            <span
                                                                title="Device PIN / Password Set"
                                                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-300"
                                                            >
                                                                <Key size={11} className="text-slate-600" />
                                                                PIN
                                                            </span>
                                                        )}
                                                        {(!emp.biometrics || emp.biometrics.total_registered === 0) && (
                                                            <span
                                                                title="Employee profile exists on machine, but biometric sensor template (Fingerprint / Face) not enrolled yet on keypad."
                                                                className="text-[11px] text-gray-400 italic"
                                                            >
                                                                ℹ️ Template pending on sensor
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="text-sm text-gray-700 space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <Mail size={14} className="text-gray-400" />
                                                        <span className="text-xs">{emp.email}</span>
                                                    </div>
                                                    {emp.mobile !== 'N/A' && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-gray-500">{emp.mobile}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                                    <Briefcase size={14} className="text-gray-400" />
                                                    {emp.designation}
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 text-sm text-gray-700">
                                                    <Building2 size={14} className="text-gray-400" />
                                                    {emp.department}
                                                </div>
                                            </td>

                                            <td className="px-4 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <button
                                                        onClick={() => handleManageShift(emp)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 hover:shadow-sm transition-all duration-200 text-xs font-semibold border border-blue-200"
                                                        title="Manage Employee Shift"
                                                    >
                                                        <Clock size={14} />
                                                        Manage Shift
                                                    </button>
                                                    <button
                                                        onClick={() => handleAddCLPL(emp)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 hover:shadow-sm transition-all duration-200 text-xs font-semibold border border-green-200"
                                                        title="Add Casual Leave / Privilege Leave"
                                                    >
                                                        <Calendar size={14} />
                                                        Add CL/PL
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenProfileModal(emp.id)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 hover:shadow-sm transition-all duration-200 text-xs font-semibold border border-purple-200"
                                                    >
                                                        <Edit size={12} />
                                                        Edit Profile
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(emp)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 hover:shadow-sm transition-all duration-200 text-xs font-semibold border border-red-200"
                                                        title="Delete Employee permanently and unbind from biometric machines"
                                                    >
                                                        <Trash size={12} />
                                                        Delete
                                                    </button>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={emp.isActive}
                                                            onChange={(e) => {
                                                                e.preventDefault();
                                                                setPendingEmployee(emp);
                                                                setPendingStatus(!emp.isActive);
                                                                setShowConfirmModal(true);
                                                            }}
                                                            className="sr-only peer"
                                                        />
                                                        <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                                        <span className="ml-2 text-xs font-medium text-gray-600">
                                                            {emp.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </label>

                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(emp.web_status)}
                                                            onClick={(e) => {
                                                                e.preventDefault(); // stop auto toggle
                                                                setPendingEmployee(emp);
                                                                setPendingStatus(emp.web_status === 1 ? 0 : 1); // correct next status
                                                                setShowConfirmModalweb(true);
                                                            }}
                                                            className="sr-only peer"
                                                            readOnly
                                                        />

                                                        <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                                                        <span className="ml-2 text-xs font-medium text-gray-600">
                                                            {emp.web_status === 1 ? 'WebStatusActive' : 'WebStatusInactive'}
                                                        </span>
                                                    </label>

                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Employee Modal - Keep as is */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 flex justify-between items-center sticky top-0">
                            <div>
                                <h2 className="text-2xl font-bold flex items-center gap-3">
                                    <Plus size={28} />
                                    Add New Employee
                                </h2>
                                <p className="text-blue-100 mt-1">Fill in the employee details</p>
                            </div>
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSubmit} className="p-6">
                            {formError && (
                                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                                    {formError}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* First Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        First Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Last Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                {/* Mobile Number */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Mobile Number <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex gap-2">
                                        <select
                                            name="country_code"
                                            value={formData.country_code}
                                            onChange={handleInputChange}
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="+91">+91</option>
                                            <option value="+1">+1</option>
                                            <option value="+44">+44</option>
                                        </select>
                                        <input
                                            type="tel"
                                            name="mobile_number"
                                            value={formData.mobile_number}
                                            onChange={handleInputChange}
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter mobile number"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Gender */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Gender <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="gender"
                                        value={formData.gender}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                {/* Date of Birth */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Date of Birth <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        name="date_of_birth"
                                        value={formData.date_of_birth}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                {/* Designation */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Designation <span className="text-red-500">*</span>
                                        {designations.length > 0 && (
                                            <span className="text-xs text-gray-500 ml-2">({designations.length} available)</span>
                                        )}
                                    </label>
                                    <select
                                        name="designation_id_FK"
                                        value={formData.designation_id_FK}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Designation</option>
                                        {designations.length > 0 ? (
                                            designations.map((designation) => {
                                                const id = designation.designation_id_PK;
                                                const name = designation.designation_name;
                                                return (
                                                    <option key={id} value={String(id)}>
                                                        {name}
                                                    </option>
                                                );
                                            })
                                        ) : (
                                            <option value="" disabled>Loading designations...</option>
                                        )}
                                    </select>
                                </div>

                                {/* Department */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Department <span className="text-red-500">*</span>
                                        {departments.length > 0 && (
                                            <span className="text-xs text-gray-500 ml-2">({departments.length} available)</span>
                                        )}
                                    </label>
                                    <select
                                        name="department_id_FK"
                                        value={formData.department_id_FK}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Department</option>
                                        {departments.length > 0 ? (
                                            departments.map((department) => {
                                                const id = department.department_id_PK;
                                                const name = department.department_name;
                                                return (
                                                    <option key={id} value={String(id)}>
                                                        {name}
                                                    </option>
                                                );
                                            })
                                        ) : (
                                            <option value="" disabled>Loading departments...</option>
                                        )}
                                    </select>
                                </div>

                                {/* Biometric Machine Credentials (Optional) */}
                                <div className="md:col-span-2 mt-2 pt-4 border-t border-slate-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                                                <CreditCard className="w-4 h-4 text-blue-600" />
                                                Biometric Machine Hardware Credentials
                                                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                                    Optional
                                                </span>
                                            </h4>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                Assign an RFID card number or keypad PIN directly from here. The cloud will push them straight to your biometric machines.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* RFID Card Number */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
                                                <span>RFID / Smart Card Number</span>
                                                <span className="text-xs font-normal text-gray-400">Optional</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="rfid_serial_number"
                                                    value={formData.rfid_serial_number || ''}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g. 12899448 or 00123456"
                                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                                />
                                                <CreditCard className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
                                            </div>
                                            <p className="text-[11px] text-gray-400 mt-1">Number printed on the physical RFID smart card.</p>
                                        </div>

                                        {/* Machine Access PIN */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
                                                <span>Machine Access PIN</span>
                                                <span className="text-xs font-normal text-gray-400">Optional</span>
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="device_password"
                                                    value={formData.device_password || ''}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g. 1234 (4-8 digits)"
                                                    maxLength={8}
                                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                                />
                                                <Key className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" />
                                            </div>
                                            <p className="text-[11px] text-gray-400 mt-1">Numeric password for attendance authentication on the device.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Live Hardware Target Status Preview */}
                            <div className="mt-4 p-3.5 rounded-xl border bg-slate-50/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-2">
                                    <Monitor size={16} className="text-slate-500" />
                                    <span className="font-semibold text-slate-700">Biometric Sync Target:</span>
                                    {biometricHealth?.has_devices ? (
                                        <span className="flex items-center gap-1.5 font-mono">
                                            <span
                                                className={`inline-block w-2.5 h-2.5 rounded-full ${
                                                    biometricHealth.assigned_devices[0]?.is_online
                                                        ? 'bg-emerald-500 animate-pulse'
                                                        : 'bg-rose-500'
                                                }`}
                                            />
                                            <span className="font-bold text-slate-900">{biometricHealth.assigned_devices[0]?.dev_id}</span>
                                            <span
                                                className={`font-semibold ${
                                                    biometricHealth.assigned_devices[0]?.is_online ? 'text-emerald-700' : 'text-rose-700'
                                                }`}
                                            >
                                                ({biometricHealth.assigned_devices[0]?.is_online ? '🟢 Online' : '🔴 Offline'})
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-amber-700 font-medium italic">No biometric machine linked to company</span>
                                    )}
                                </div>
                                <span className="text-[11px] text-slate-500">
                                    {biometricHealth?.has_devices
                                        ? biometricHealth.assigned_devices[0]?.is_online
                                            ? '⚡ Will push immediately on save (~5-15s)'
                                            : '☁️ Will queue in cloud & push on device WiFi reconnect'
                                        : '⚪ Saved to cloud database only'}
                                </span>
                            </div>

                            {/* Form Actions */}
                            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${saving
                                        ? 'bg-blue-300 text-white cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    <Save size={18} />
                                    {saving ? 'Adding...' : 'Add Employee'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Manage Shift Modal - Keep as is */}
            {showShiftModal && selectedEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-6 flex justify-between items-center sticky top-0">
                            <div>
                                <h2 className="text-2xl font-bold flex items-center gap-3">
                                    <Clock size={28} />
                                    Manage Shift - {selectedEmployee.name}
                                </h2>
                                <p className="text-blue-100 mt-1">
                                    {hasShift ? 'Update shift details' : 'Create new shift'}
                                </p>
                            </div>
                            <button
                                onClick={() => setShowShiftModal(false)}
                                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <div className="p-6">
                            {shiftError && (
                                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                                    {shiftError}
                                </div>
                            )}

                            {shiftLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Shift Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Shift Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={shiftData.shift_name}
                                            onChange={(e) => {
                                                setShiftData(prev => ({ ...prev, shift_name: e.target.value }));
                                                setShiftError('');
                                            }}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="e.g., Morning Shift, Night Shift"
                                            required
                                        />
                                    </div>

                                    {/* Time Range */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                Start Time <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={shiftData.start_time}
                                                onChange={(e) => {
                                                    setShiftData(prev => ({ ...prev, start_time: e.target.value }));
                                                    setShiftError('');
                                                }}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                End Time <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="time"
                                                value={shiftData.end_time}
                                                onChange={(e) => {
                                                    setShiftData(prev => ({ ...prev, end_time: e.target.value }));
                                                    setShiftError('');
                                                }}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Working Days */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Working Days <span className="text-red-500">*</span>
                                        </label>
                                        <div className="grid grid-cols-7 gap-2">
                                            {weekDays.map((day) => (
                                                <button
                                                    key={day}
                                                    type="button"
                                                    onClick={() => handleWorkingDayToggle(day)}
                                                    className={`px-3 py-2 rounded-lg border-2 transition-all font-medium ${shiftData.working_days.includes(day)
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                                                        }`}
                                                >
                                                    {day}
                                                </button>
                                            ))}
                                        </div>
                                        {shiftData.working_days.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-2">
                                                Selected: {shiftData.working_days.join(', ')}
                                            </p>
                                        )}
                                    </div>

                                    {/* Form Actions */}
                                    <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => setShowShiftModal(false)}
                                            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                            disabled={shiftSaving}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSaveShift}
                                            disabled={shiftSaving}
                                            className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${shiftSaving
                                                ? 'bg-blue-300 text-white cursor-not-allowed'
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                                }`}
                                        >
                                            <Save size={18} />
                                            {shiftSaving ? 'Saving...' : hasShift ? 'Update Shift' : 'Create Shift'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}


            {/* Delete Employee Confirmation Modal */}
            {showDeleteModal && employeeToDelete && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-red-100 animate-in fade-in duration-200">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-red-600 to-rose-700 text-white p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white shrink-0">
                                    <Trash size={20} />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold">Delete Employee</h2>
                                    <p className="text-red-100 text-xs mt-0.5">Permanent account and hardware removal</p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <p className="text-gray-800 font-medium mb-2">
                                Are you sure you want to permanently delete <span className="font-bold text-red-600">{employeeToDelete.name}</span> ({employeeToDelete.employeeId})?
                            </p>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 space-y-1.5 mt-3">
                                <p className="font-semibold flex items-center gap-1.5 text-amber-900">
                                    <AlertTriangle size={14} /> This action will also:
                                </p>
                                <ul className="list-disc list-inside space-y-0.5 pl-1 text-[11px] text-amber-800">
                                    <li>Send a <code className="font-mono bg-amber-100 px-1 rounded">DELETE_USER</code> command to all company biometric machines</li>
                                    <li>Delete machine templates (card, password, fingerprint links)</li>
                                    <li>Remove employee from active attendance & shift registers</li>
                                </ul>
                            </div>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setEmployeeToDelete(null);
                                }}
                                disabled={deletingEmployee}
                                className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-white text-gray-700 transition font-medium text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={deletingEmployee}
                                className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white rounded-xl transition font-semibold text-sm shadow-lg shadow-red-500/20 flex items-center gap-2"
                            >
                                <Trash size={16} className={deletingEmployee ? 'animate-spin' : ''} />
                                {deletingEmployee ? 'Deleting...' : 'Delete Employee'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal - Keep as is */}
            {showConfirmModal && pendingEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-6 rounded-t-2xl">
                            <h2 className="text-xl font-bold flex items-center gap-3">
                                Confirm Status Change
                            </h2>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <p className="text-gray-700 mb-4">
                                Are you sure you want to <span className="font-semibold text-blue-600">
                                    {pendingStatus ? 'activate' : 'deactivate'}
                                </span> the employee <span className="font-semibold">{pendingEmployee.name}</span>?
                            </p>
                            <p className="text-sm text-gray-500">
                                {pendingStatus
                                    ? 'The employee will be able to access the system.'
                                    : 'The employee will not be able to access the system.'}
                            </p>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowConfirmModal(false);
                                    setPendingEmployee(null);
                                    setPendingStatus(null);
                                }}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmStatusChange}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Confirmation Modal - Keep as is */}
            {showConfirmModalweb && pendingEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-6 rounded-t-2xl">
                            <h2 className="text-xl font-bold flex items-center gap-3">
                                Confirm Status Change
                            </h2>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <p className="text-gray-700 mb-4">
                                Are you sure you want to <span className="font-semibold text-blue-600">
                                    {pendingStatus ? 'WebStatusaActivate' : 'WebStatusDeactivate'}
                                </span> the employee <span className="font-semibold">{pendingEmployee.name}</span>?
                            </p>
                            <p className="text-sm text-gray-500">
                                {pendingStatus
                                    ? 'The employee will be able to access the system.'
                                    : 'The employee will not be able to access the system.'}
                            </p>
                        </div>

                        {/* Modal Actions */}
                        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
                            <button
                                onClick={() => {
                                    setShowConfirmModalweb(false);
                                    setPendingEmployee(null);
                                    setPendingStatus(null);
                                }}
                                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmWebChakingStatusChange}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add CL/PL Modal - Keep as is */}
            {showCLPLModal && clplEmployee && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r  from-blue-600 to-indigo-700 text-white p-6 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold flex items-center gap-3">
                                        <Calendar size={24} />
                                        Add CL/PL - {clplEmployee.name}
                                    </h2>
                                    <p className="text-green-100 mt-1 text-sm">Set quarterly leave balance</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowCLPLModal(false);
                                        setClplEmployee(null);
                                        setClplError('');
                                    }}
                                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Modal Form */}
                        <div className="p-6">
                            {clplError && (
                                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
                                    {clplError}
                                </div>
                            )}

                            <div className="space-y-4">
                                {/* Quarter */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Quarter <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={clplData.quarter}
                                        onChange={(e) => {
                                            setClplData(prev => ({ ...prev, quarter: e.target.value }));
                                            setClplError('');
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="Q1">Q1 (April, May, June)</option>
                                        <option value="Q2">Q2 (July, August, September)</option>
                                        <option value="Q3">Q3 (October, November, December)</option>
                                        <option value="Q4">Q4 (January, February, March)</option>
                                    </select>
                                </div>

                                {/* Year */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Year <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={clplData.year}
                                        onChange={(e) => {
                                            setClplData(prev => ({ ...prev, year: e.target.value }));
                                            setClplError('');
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        min="2020"
                                        max="2100"
                                        required
                                    />
                                </div>

                                {/* Total CL */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Total CL (Casual Leave) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={clplData.totalCL}
                                        onChange={(e) => {
                                            setClplData(prev => ({ ...prev, totalCL: e.target.value }));
                                            setClplError('');
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        min="0"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Default quarterly allowance: 2 CL</p>
                                </div>

                                {/* Total PL */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Total PL (Privilege Leave) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={clplData.totalPL}
                                        onChange={(e) => {
                                            setClplData(prev => ({ ...prev, totalPL: e.target.value }));
                                            setClplError('');
                                        }}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                        min="0"
                                        required
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Default quarterly allowance: 2 PL</p>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCLPLModal(false);
                                        setClplEmployee(null);
                                        setClplError('');
                                    }}
                                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    disabled={clplSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveCLPL}
                                    disabled={clplSaving}
                                    className={`px-6 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors ${clplSaving
                                        ? 'bg-blue-600 text-white cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-indigo-700'
                                        }`}
                                >
                                    <Save size={18} />
                                    {clplSaving ? 'Saving...' : 'Save CL/PL'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------------- EDIT PROFILE MODAL ---------------- */}
            {showEditModal && profileData && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
                    <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl relative">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-t-xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-2xl font-bold">Edit Employee Profile</h2>
                                    <p className="text-blue-100 mt-1">Update employee information</p>
                                </div>
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 max-h-[70vh] overflow-y-auto">
                            <div className="flex justify-center mb-6">
                                <div className="relative" style={{ display: 'inline-block' }}>
                                    <ProfileImageUpload
                                        userId={selectedEmployeeId}
                                        token={localStorage.getItem("token")}
                                        onImageUpdate={() => fetchEmployees()}
                                    />
                                    <label
                                        htmlFor="profile-image-upload"
                                        className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 p-2 rounded-full shadow-lg cursor-pointer border-2 border-white transition-colors flex items-center justify-center"
                                        style={{ transform: 'translate(25%, 25%)', zIndex: 10 }}
                                        title="Change Image"
                                    >
                                        <Edit size={16} className="text-white" />
                                    </label>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Read-only Fields */}
                                <InputField label="First Name" value={profileData.first_name} disabled />
                                <InputField label="Last Name" value={profileData.last_name} disabled />
                                <InputField label="Email" value={profileData.email} disabled />
                                <InputField label="Gender" value={profileData.gender} disabled />
                                <InputField label="Date of Birth" value={profileData.date_of_birth} disabled />
                                <InputField label="Department" value={profileData.department_name} disabled />
                                <InputField label="Designation" value={profileData.designation_name} disabled />
                                <InputField label="Blood Group" value={profileData.blood_group} disabled />

                                {/* Editable Fields */}
                                {/* <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        RFID Serial Number
                                    </label>

                                    <select
                                        className={`w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 
        focus:ring-2 focus:ring-blue-500 focus:border-transparent
        ${profileData.rfid_serial_number ? "bg-gray-100 cursor-not-allowed" : ""}`}

                                        value={profileData.rfid_serial_number || ""}

                                        disabled={!!profileData.rfid_serial_number} 

                                        onChange={(e) =>
                                            setProfileData({
                                                ...profileData,
                                                rfid_serial_number: e.target.value
                                            })
                                        }
                                    >
                                        <option value="">
                                            {profileData.rfid_serial_number
                                                ? profileData.rfid_serial_number
                                                : "Select RFID"}
                                        </option>

                                        {rfidList.map((r) => (
                                            <option key={r.id} value={r.rfid_serial_number}>
                                                {r.rfid_serial_number}
                                            </option>
                                        ))}
                                    </select>


                                </div> */}

                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
                                        <span>RFID / Smart Card Number</span>
                                        <span className="text-xs font-normal text-gray-400">Optional</span>
                                    </label>

                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            placeholder="Enter card number (e.g. 12899448)"
                                            value={profileData.rfid_serial_number || ""}
                                            onChange={(e) =>
                                                setProfileData({
                                                    ...profileData,
                                                    rfid_serial_number: e.target.value
                                                })
                                            }
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />

                                        {rfidList.length > 0 && (
                                            <select
                                                className="border border-gray-300 rounded-lg px-2 py-2 text-xs bg-slate-50 focus:ring-2 focus:ring-blue-500"
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        setProfileData({
                                                            ...profileData,
                                                            rfid_serial_number: e.target.value
                                                        });
                                                    }
                                                }}
                                                value=""
                                            >
                                                <option value="">Pick from Stock</option>
                                                {rfidList.map((r) => (
                                                    <option key={r.id} value={r.rfid_serial_number}>
                                                        {r.rfid_serial_number}
                                                    </option>
                                                ))}
                                            </select>
                                        )}

                                        {profileData.rfid_serial_number && (
                                            <button
                                                type="button"
                                                onClick={handleDeleteRfid}
                                                className="p-2 border border-red-200 rounded-lg text-red-600 hover:bg-red-50"
                                                title="Remove Card from Employee"
                                            >
                                                <Trash size={18} />
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-gray-400 mt-1">Smart card number for tap-in punch.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center justify-between">
                                        <span>Machine Access PIN</span>
                                        <span className="text-xs font-normal text-gray-400">Optional</span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            maxLength={8}
                                            placeholder="e.g. 1234 (4-8 digits)"
                                            value={profileData.device_password || ""}
                                            onChange={(e) =>
                                                setProfileData({
                                                    ...profileData,
                                                    device_password: e.target.value
                                                })
                                            }
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-9 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        {profileData.device_password && (
                                            <button
                                                type="button"
                                                onClick={() => setProfileData({ ...profileData, device_password: "" })}
                                                className="absolute right-2.5 top-2.5 text-gray-400 hover:text-red-500"
                                                title="Clear PIN"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-[11px] text-gray-400 mt-1">Keypad PIN for attendance punches on the machine.</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Mobile Number
                                    </label>
                                    <div className="flex gap-2">
                                        <select
                                            value={profileData.country_code || '+91'}
                                            onChange={(e) =>
                                                setProfileData({
                                                    ...profileData,
                                                    country_code: e.target.value
                                                })
                                            }
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        >
                                            <option value="+91">+91</option>
                                            <option value="+1">+1</option>
                                            <option value="+44">+44</option>
                                        </select>
                                        <input
                                            type="tel"
                                            value={profileData.mobile_number || ''}
                                            onChange={(e) =>
                                                setProfileData({
                                                    ...profileData,
                                                    mobile_number: e.target.value
                                                })
                                            }
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="Enter mobile number"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <InputField
                                        label="Emergency Contact Number"
                                        value={profileData.emergency_contact_no}
                                        onChange={(e) =>
                                            setProfileData({
                                                ...profileData,
                                                emergency_contact_no: e.target.value
                                            })
                                        }
                                    />
                                </div>

                                {/* Fingerprint */}
                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Fingerprint
                                    </label>
                                    <select
                                        className={`w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${profileData.fingerprint ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                        value={profileData.fingerprint || ""}
                                        disabled={!!profileData.fingerprint}
                                        onChange={(e) => setProfileData({ ...profileData, fingerprint: e.target.value })}
                                    >
                                        <option value="">{profileData.fingerprint ? profileData.fingerprint : "Select Fingerprint"}</option>
                                        {rfidList.map((r, idx) => r.fingerprint ? (
                                            <option key={`fp-${idx}`} value={r.fingerprint}>{r.fingerprint}</option>
                                        ) : null)}
                                    </select>
                                    {profileData.fingerprint && (
                                        <button type="button" onClick={() => { if (window.confirm("Are you sure you want to remove this Fingerprint?")) setProfileData(prev => ({ ...prev, fingerprint: '' })) }} className="absolute right-3 top-10 text-red-600 hover:text-red-800" title="Remove Fingerprint">
                                            <Trash size={20} />
                                        </button>
                                    )}
                                </div>

                                {/* Face Login */}
                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Face Login
                                    </label>
                                    <select
                                        className={`w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${profileData.face_login ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                        value={profileData.face_login || ""}
                                        disabled={!!profileData.face_login}
                                        onChange={(e) => setProfileData({ ...profileData, face_login: e.target.value })}
                                    >
                                        <option value="">{profileData.face_login ? profileData.face_login : "Select Face Login"}</option>
                                        {rfidList.map((r, idx) => r.face_login ? (
                                            <option key={`face-${idx}`} value={r.face_login}>{r.face_login}</option>
                                        ) : null)}
                                    </select>
                                    {profileData.face_login && (
                                        <button type="button" onClick={() => { if (window.confirm("Are you sure you want to remove this Face Login?")) setProfileData(prev => ({ ...prev, face_login: '' })) }} className="absolute right-3 top-10 text-red-600 hover:text-red-800" title="Remove Face Login">
                                            <Trash size={20} />
                                        </button>
                                    )}
                                </div>

                                {/* Palm Login */}
                                <div className="relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Palm Login
                                    </label>
                                    <select
                                        className={`w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${profileData.palm_login ? "bg-gray-100 cursor-not-allowed" : ""}`}
                                        value={profileData.palm_login || ""}
                                        disabled={!!profileData.palm_login}
                                        onChange={(e) => setProfileData({ ...profileData, palm_login: e.target.value })}
                                    >
                                        <option value="">{profileData.palm_login ? profileData.palm_login : "Select Palm Login"}</option>
                                        {rfidList.map((r, idx) => r.palm_login ? (
                                            <option key={`palm-${idx}`} value={r.palm_login}>{r.palm_login}</option>
                                        ) : null)}
                                    </select>
                                    {profileData.palm_login && (
                                        <button type="button" onClick={() => { if (window.confirm("Are you sure you want to remove this Palm Login?")) setProfileData(prev => ({ ...prev, palm_login: '' })) }} className="absolute right-3 top-10 text-red-600 hover:text-red-800" title="Remove Palm Login">
                                            <Trash size={20} />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Live Hardware Target Status Preview */}
                            <div className="mt-4 p-3.5 rounded-xl border bg-slate-50/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-2">
                                    <Monitor size={16} className="text-slate-500" />
                                    <span className="font-semibold text-slate-700">Biometric Sync Target:</span>
                                    {biometricHealth?.has_devices ? (
                                        <span className="flex items-center gap-1.5 font-mono">
                                            <span
                                                className={`inline-block w-2.5 h-2.5 rounded-full ${
                                                    biometricHealth.assigned_devices[0]?.is_online
                                                        ? 'bg-emerald-500 animate-pulse'
                                                        : 'bg-rose-500'
                                                }`}
                                            />
                                            <span className="font-bold text-slate-900">{biometricHealth.assigned_devices[0]?.dev_id}</span>
                                            <span
                                                className={`font-semibold ${
                                                    biometricHealth.assigned_devices[0]?.is_online ? 'text-emerald-700' : 'text-rose-700'
                                                }`}
                                            >
                                                ({biometricHealth.assigned_devices[0]?.is_online ? '🟢 Online' : '🔴 Offline'})
                                            </span>
                                        </span>
                                    ) : (
                                        <span className="text-amber-700 font-medium italic">No biometric machine linked to company</span>
                                    )}
                                </div>
                                <span className="text-[11px] text-slate-500">
                                    {biometricHealth?.has_devices
                                        ? biometricHealth.assigned_devices[0]?.is_online
                                            ? '⚡ Profile changes will push immediately'
                                            : '☁️ Updates queued until device reconnects'
                                        : '⚪ Cloud database only'}
                                </span>
                            </div>

                            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setShowEditModal(false)}
                                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                    className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${saving
                                        ? 'bg-blue-300 text-white cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    <Save size={18} />
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔔 Real-Time Feedback Notification Toast */}
            {feedbackToast && (
                <div className="fixed bottom-6 right-6 z-50 max-w-md transition-all duration-300 ease-out transform translate-y-0">
                    <div
                        className={`p-4 rounded-2xl shadow-2xl border flex items-start gap-3 backdrop-blur-md ${
                            feedbackToast.type === 'success'
                                ? 'bg-slate-900/95 text-white border-emerald-500/50 shadow-emerald-950/20'
                                : feedbackToast.type === 'warning'
                                ? 'bg-slate-900/95 text-white border-amber-500/50 shadow-amber-950/20'
                                : feedbackToast.type === 'error'
                                ? 'bg-slate-900/95 text-white border-rose-500/50 shadow-rose-950/20'
                                : 'bg-slate-900/95 text-white border-blue-500/50'
                        }`}
                    >
                        <div className="mt-0.5 shrink-0">
                            {feedbackToast.type === 'success' ? (
                                <CheckCircle2 size={20} className="text-emerald-400" />
                            ) : feedbackToast.type === 'warning' ? (
                                <AlertTriangle size={20} className="text-amber-400" />
                            ) : feedbackToast.type === 'error' ? (
                                <AlertCircle size={20} className="text-rose-400" />
                            ) : (
                                <Info size={20} className="text-blue-400" />
                            )}
                        </div>
                        <div className="flex-1 pr-2">
                            <h4 className="text-sm font-bold text-white">{feedbackToast.title}</h4>
                            <p className="text-xs text-gray-200 mt-1 leading-relaxed">{feedbackToast.message}</p>
                        </div>
                        <button
                            onClick={() => setFeedbackToast(null)}
                            className="text-gray-400 hover:text-white transition-colors"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageEmployees;


